import { MiniGameBase } from './base.js';

/**
 * "Outlier" identification mini-game
 * Player clicks to select the item that doesn't belong
 * Immediate feedback with explanation
 */
export class OutlierGame extends MiniGameBase {
    constructor(deepDiveData, categoryName, onComplete = null, category = null) {
        super(deepDiveData, categoryName, onComplete, category);
        this.selectedItem = null;
        this.completed = false;
    }

    /**
     * Render the outlier selection UI
     */
    render(container) {
        super.render(container);

        container.innerHTML = `
            <div class="deepdive-container">
                <div class="instruction">${this.data.instruction || 'Which item is the outlier?'}</div>
                <div class="outlier-grid">
                    ${this.data.items.map((item, idx) => `
                        <div class="outlier-item ${this.completed && idx === this.data.outlierIndex ? 'correct' : ''}" data-index="${idx}">
                            ${item}
                        </div>
                    `).join('')}
                </div>
                <div class="feedback-message" style="display: none;"></div>
                <div class="button-row"></div>
            </div>
        `;

        // Add skip button
        const buttonRow = container.querySelector('.button-row');
        const skipBtn = this.createSkipButton();
        buttonRow.appendChild(skipBtn);

        this.attachEventListeners();
    }

    /**
     * Attach click listeners to items
     */
    attachEventListeners() {
        if (this.completed) return;

        const items = this.container.querySelectorAll('.outlier-item');

        items.forEach((el, idx) => {
            el.addEventListener('click', () => this.handleItemClick(idx));

            // Attach hint if this item matches a word in category
            const itemText = this.data.items[idx];
            if (itemText) {
                this.attachHintIfExists(el, itemText);
            }
        });
    }

    /**
     * Handle item click
     */
    handleItemClick(index) {
        if (this.completed) {
            return { action: 'already_complete' };
        }

        this.playSelectSound();
        this.selectedItem = index;

        // Check if correct
        if (index === this.data.outlierIndex) {
            // Correct!
            this.playMatchSound();
            this.completed = true;
            this.completedItems = [index];
            this.showSuccess();
            return { action: 'correct' };
        } else {
            // Incorrect
            this.playErrorSound();
            this.showError(index);
            return { action: 'incorrect' };
        }
    }

    /**
     * Show success feedback
     */
    showSuccess() {
        const feedback = this.container.querySelector('.feedback-message');
        const items = this.container.querySelectorAll('.outlier-item');

        feedback.style.display = 'block';
        feedback.className = 'feedback-message success';
        feedback.textContent = this.data.explanation || 'Correct!';

        // Highlight correct item
        items[this.data.outlierIndex]?.classList.add('correct');

        // Disable all items
        items.forEach(item => {
            item.style.pointerEvents = 'none';
        });

        setTimeout(() => this.showCelebration(), 1500);
    }

    /**
     * Show error feedback for specific item
     */
    showError(index) {
        const feedback = this.container.querySelector('.feedback-message');
        const items = this.container.querySelectorAll('.outlier-item');

        feedback.style.display = 'block';
        feedback.className = 'feedback-message error';
        feedback.textContent = 'Not quite. Try again!';

        // Add error animation to clicked item
        items[index]?.classList.add('error');

        setTimeout(() => {
            items[index]?.classList.remove('error');
            feedback.style.display = 'none';
        }, 1500);
    }

    /**
     * Handle generic input (for testing)
     */
    handleInput(selection) {
        if ('item' in selection) {
            return this.handleItemClick(selection.item);
        }
        return { action: 'none' };
    }

    /**
     * Check if game is complete
     */
    isComplete() {
        return this.completed;
    }

    /**
     * Check if current answer is correct
     */
    checkAnswer() {
        return this.selectedItem === this.data.outlierIndex;
    }

    /**
     * Show celebration
     */
    showCelebration() {
        super.showCelebration();
        // Could add more elaborate celebration here
    }

    /**
     * Get progress string
     */
    getProgress() {
        return this.completed ? '1/1' : '0/1';
    }
}
